import { ApiError, listArchiveEvents, listEvents, type ArchiveEventItem, type Event } from "@/api";
import { Badge } from "@/components/ui/Badge";
import { VorgangList } from "@/components/VorgangList";
import { Button } from "@/components/ui/Button";
import { DeliveryProgressBar } from "@/components/ui/DeliveryProgressBar";
import { formatDateTime } from "@/lib/format";
import {
  EVENT_TYPE_LABEL,
  eventStatusLabel,
  isOpenEvent,
  lastMailLabel,
  statusTone,
  typeTone,
} from "@/lib/labels";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

export function MailHomePage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState<Event[] | null>(null);
  const [sent, setSent] = useState<ArchiveEventItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([listEvents(), listArchiveEvents({ limit: 10 })])
      .then(([all, archive]) => {
        if (cancelled) return;
        setEvents(all);
        setSent(archive.items);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : "Mails konnten nicht geladen werden.");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const running = (events ?? []).filter(
    (event) => (event.type === "incident" || event.type === "maintenance") && isOpenEvent(event.status),
  );
  const drafts = (events ?? []).filter((event) => event.status === "draft");

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-4">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-base font-semibold">Mails</h1>
        </div>
        <Button variant="primary" onClick={() => navigate("/mails/new")}>
          Neue Mail
        </Button>
      </div>
      {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}

      {events === null && !error ? (
        <div className="space-y-2">
          <div className="h-16 animate-pulse rounded-md bg-surface-subtle" />
          <div className="h-16 animate-pulse rounded-md bg-surface-subtle" />
        </div>
      ) : null}

      {running.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold">Läuft noch</h2>
          <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
            {running.map((event) => (
              <li key={event.id}>
                <Link
                  to={`/events/${event.id}`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-surface-subtle"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{event.title || "Ohne Titel"}</div>
                    <div className="text-[11px] text-muted">{lastMailLabel(event.last_phase, event.sent_count ?? 0)}</div>
                    <DeliveryProgressBar progress={event.delivery_progress} className="mt-1 max-w-xs" />
                  </div>
                  <Badge tone={typeTone(event.type)}>{EVENT_TYPE_LABEL[event.type]}</Badge>
                  <Badge tone={statusTone(event.status)}>{eventStatusLabel(event)}</Badge>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {drafts.length > 0 ? (
        <section className="mb-6">
          <h2 className="mb-2 text-sm font-semibold">Entwürfe</h2>
          <ul className="divide-y divide-hairline overflow-hidden rounded-lg border border-hairline bg-surface">
            {drafts.map((event) => (
              <li key={event.id}>
                <Link
                  to={`/events/${event.id}`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-surface-subtle"
                >
                  <div className="min-w-0 flex-1 truncate text-sm font-medium">
                    {event.title || "Ohne Titel"}
                  </div>
                  <span className="text-[11px] text-muted tabular-nums">{formatDateTime(event.updated_at)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <section>
        <h2 className="mb-2 text-sm font-semibold">Zuletzt gesendet</h2>
        {sent && sent.length === 0 && drafts.length === 0 && running.length === 0 ? (
          <div className="rounded-lg border border-dashed border-hairline bg-surface px-6 py-12 text-center">
            <p className="text-sm text-muted">Noch keine Mail.</p>
            <Button variant="primary" className="mt-4" onClick={() => navigate("/mails/new")}>
              Erste Mail schreiben
            </Button>
          </div>
        ) : null}
        {sent && sent.length === 0 && (drafts.length > 0 || running.length > 0) ? (
          <p className="text-sm text-muted">Noch nichts gesendet.</p>
        ) : null}
        {sent && sent.length > 0 ? (
          <VorgangList items={sent} expandFirstIfMulti />
        ) : null}
        <p className="mt-2 text-[11px] text-muted">
          <Link to="/archive" className="hover:text-ink">
            Alle gesendeten Mails im Archiv
          </Link>
        </p>
      </section>
      <p className="mt-6 text-[11px] text-muted">
        <Link to="/board" className="hover:text-ink">
          Störungen und Wartungen als Board
        </Link>
      </p>
    </div>
  );
}
